import type { TooltipItem } from "chart.js";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import { useMemo } from "react";
import type { Estimation } from "@/interfaces/estimation.interface";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface IssueRateChartProps {
  estimations: Estimation[];
}

export const IssueRateChart = ({ estimations }: IssueRateChartProps) => {
  const chartData = useMemo(() => {
    const groupedByDate = estimations.reduce(
      (acc, estimation) => {
        //  Old data doesnt have analytics, so we just skip it!
        if (!estimation.analytics) return acc;

        const date = estimation.dateCreated;
        if (!acc[date]) {
          acc[date] = {
            totalRate: 0,
            count: 0,
            totalImprovement: 0,
            totalFalsePositive: 0,
            totalUpdatedIssueRate: 0,
            countWithPostProcessing: 0,
          };
        }
        acc[date].totalRate += estimation.analytics.issueRate;
        acc[date].count += 1;

        // Check for analyticsPostProcessing and add values
        if (estimation.analyticsPostProcessing) {
          acc[date].totalImprovement += estimation.analyticsPostProcessing.improvementPercentage || 0;
          acc[date].totalFalsePositive += estimation.analyticsPostProcessing.falsePositiveRate || 0;

          // Calculate updated issue rate
          const updatedIssueRate =
            (estimation.analyticsPostProcessing.updatedIssueCount / estimation.analytics.totalNumberOfLineItems) * 100;
          acc[date].totalUpdatedIssueRate += updatedIssueRate;
          acc[date].countWithPostProcessing += 1;
        }

        return acc;
      },
      {} as Record<
        string,
        {
          totalRate: number;
          count: number;
          totalImprovement: number;
          totalFalsePositive: number;
          totalUpdatedIssueRate: number;
          countWithPostProcessing: number;
        }
      >,
    );

    const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    const averageRates = sortedDates.map((date) => ({
      date,
      rate: groupedByDate[date].totalRate / groupedByDate[date].count,
      improvement: groupedByDate[date].totalImprovement / groupedByDate[date].count,
      falsePositive: groupedByDate[date].totalFalsePositive / groupedByDate[date].count,
      updatedIssueRate:
        groupedByDate[date].countWithPostProcessing > 0
          ? groupedByDate[date].totalUpdatedIssueRate / groupedByDate[date].countWithPostProcessing
          : null,
    }));

    return {
      labels: averageRates.map((item) => item.date),
      datasets: [
        {
          label: "Average Issue Rate (%)",
          data: averageRates.map((item) => item.rate),
          borderColor: "rgb(75, 192, 192)",
          backgroundColor: "rgba(75, 192, 192, 0.5)",
          tension: 0.1,
        },
        {
          label: "Average Updated Issue Rate (%)",
          data: averageRates.map((item) => item.updatedIssueRate),
          borderColor: "rgb(153, 102, 255)",
          backgroundColor: "rgba(153, 102, 255, 0.5)",
          tension: 0.1,
        },
        {
          label: "Average Improvement Percentage (%)",
          data: averageRates.map((item) => item.improvement),
          borderColor: "rgb(54, 162, 235)",
          backgroundColor: "rgba(54, 162, 235, 0.5)",
          tension: 0.1,
        },
        {
          label: "Average False Positive Rate (%)",
          data: averageRates.map((item) => item.falsePositive),
          borderColor: "rgb(255, 99, 132)",
          backgroundColor: "rgba(255, 99, 132, 0.5)",
          tension: 0.1,
        },
      ],
    };
  }, [estimations]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
      },
      title: {
        display: true,
        text: "Average Issue Rate Over Time",
      },
      tooltip: {
        callbacks: {
          //  I want to show the number of issues and the number of line items
          label: function (context: TooltipItem<"line">) {
            const estimation = estimations.filter((e) => e.dateCreated === context.label && e.analytics);
            const yValue = context.parsed.y ?? 0;
            if (!estimation) return `Issue Rate: ${yValue.toFixed(2)}%`;

            return `Issue Rate: ${yValue.toFixed(2)}% \n Number of Estimates: ${estimation.length}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Issue Rate (%)",
        },
        max: 100,
      },
      x: {
        title: {
          display: true,
          text: "Date",
        },
      },
    },
  };

  return (
    <div className="h-[600px] w-full rounded-lg bg-white p-4 shadow">
      <Line options={options} data={chartData} />
    </div>
  );
};
